import React from 'react';
import {Component} from 'react';
import {observer} from 'mobx-react';
import classNames from 'classnames';
import {CheckBox, Dropdown} from '@priority-software/priority-style-react';
import {highlightText} from '../../library/utils/highlightText';
import {InjectedSearchProps} from '../../library';
import {SearchControlProps} from '../../Searches/components/Controls/SearchControl';
import {getLocalizedText, texts} from '../../../constants/TextConst';
import {getItemValue} from '../../../components/Common/FieldSearch/utils/search';
import {isEnglish} from '../../../Localization';
import {Label} from '../../../components/Common/Label';
import {NewTriggerRow, OldTriggerRow} from './SearchOptionsTemplates/TriggerRow';
import {OptionsList} from './SearchOptionsTemplates/OptionsList';
import {StatusCircle} from './StatusCircle/StatusCircle';
import styles from '../../../components/Common/FieldSearch/components/SearchOptionsV2/SearchOptions.module.scss';
import {OptionsHeader} from './OptionsHeader';

function getStrings(item, searchByTitle) {
    // reverse the strings order for english - so the english Description will appear before the description
    if (isEnglish() && !searchByTitle) {
        return {...item, string2: item.string3, string3: item.string2};
    }
    return item;
}

function needsLtr(text: string) {
    if (text) {
        return text.match(/[0-9].[a-z]|<|>/i);
    }
    return false;
}

export interface MultiSelectProps {
    isMultiSelect: boolean;
    isValueChecked?(value): boolean;
    onApprove: () => void;
}

export interface OptionsProps {
    startIndex: number;
    autoHeightMax?: number;
    onClose?: (e?) => void;
}

@observer
export class SearchOptions extends Component<
    InjectedSearchProps & SearchControlProps & OptionsProps & MultiSelectProps,
    null
> {
    renderDropdownItem = ({item, index, isLastIndex}) => {
        const {onChoose, isValueChecked, startIndex, searchByTitle, isMultiSelect} = this.props;

        const isChecked = isValueChecked?.(getItemValue(item));
        /**
         * we add the forceLrt class to a string when it marches a specific pattern
         * so that it will be displayed properly when we're in a rtl design in general
         */
        return (
            <Dropdown.Item
                key={index}
                index={startIndex + index}
                divider={!isLastIndex}
                closeOnClick={false}
                onClick={event => onChoose(getItemValue(item), event)}
                componentClasses={styles.optionsItem}
                isItemChecked={isChecked}
            >
                {isMultiSelect && (
                    <CheckBox componentClasses={styles.optionsCheckbox} checked={isChecked} />
                )}
                <div className={styles.itemRow}>
                    {searchByTitle ? (
                        <OldTriggerRow {...this.props} {...getStrings(item, searchByTitle)} />
                    ) : (
                        <NewTriggerRow {...this.props} {...getStrings(item, searchByTitle)} />
                    )}
                </div>
            </Dropdown.Item>
        );
    };

    render() {
        const {searchByTitle, handleSearchByChange} = this.props;

        return (
            <>
                {searchByTitle && (
                    <div className="search-header-container">
                        {
                            <React.Fragment>
                                <Dropdown.Header
                                    title={`${getLocalizedText(
                                        texts.searchPrefix
                                    )} ${searchByTitle}`}
                                />
                                <Label
                                    componentClasses={styles.underline}
                                    textColor="primary-500"
                                    onClick={handleSearchByChange}
                                >
                                    {getLocalizedText(texts.changeSearchBy)}
                                </Label>
                            </React.Fragment>
                        }
                    </div>
                )}
                <OptionsList
                    {...this.props}
                    gradient
                    renderDropdownItem={this.renderDropdownItem}
                />
            </>
        );
    }
}

@observer
export class ChooseOptions extends Component<
    InjectedSearchProps & SearchControlProps & OptionsProps & MultiSelectProps,
    null
> {
    renderDropdownItem = ({item, index}) => {
        const {
            onChoose,
            isValueChecked,
            searchQuery,
            startIndex,
            searchFromBeginning,
            ignorecase,
            results,
            isMultiSelect,
            field
        } = this.props;

        const isChecked = isValueChecked?.(getItemValue(item));

        return (
            <Dropdown.Item
                key={index}
                divider={index !== results.length - 1}
                index={startIndex + index}
                closeOnClick={false}
                onClick={event => onChoose(getItemValue(item), event)}
                componentClasses={styles.optionsItem}
                isItemChecked={isChecked}
            >
                <div className={styles.itemRow}>
                    {field?.isStatus && <StatusCircle status={item.string1} />}

                    <div className={styles.groupItems}>
                        {isMultiSelect && <CheckBox checked={isChecked} />}

                        <Label
                            textColor="primary-700"
                            skin="paragraph-2"
                            componentClasses={classNames(styles.optionsLabel, {
                                [styles.forceLtr]: needsLtr(item.string1)
                            })}
                        >
                            {searchQuery
                                ? highlightText(
                                      item.string1,
                                      searchQuery,
                                      ignorecase,
                                      searchFromBeginning,
                                      true
                                  )
                                : item.string1}
                        </Label>
                    </div>

                    {item.string2 && (
                        <Label
                            textColor="secondary-400"
                            skin="paragraph-2"
                            componentClasses={classNames(styles.optionsLabel, styles.margin, {
                                [styles.forceLtr]: needsLtr(item.string2)
                            })}
                        >
                            {searchQuery
                                ? highlightText(
                                      item.string2,
                                      searchQuery,
                                      ignorecase,
                                      searchFromBeginning,
                                      true
                                  )
                                : item.string2}
                        </Label>
                    )}
                </div>
            </Dropdown.Item>
        );
    };

    render() {
        const {title1, title2, isDesc, handleSortByChange, sortBy} = this.props;

        return (
            <>
                <OptionsHeader
                    title1={title1}
                    title2={title2}
                    sortBy={sortBy}
                    isDesc={isDesc}
                    handleSortByChange={handleSortByChange}
                />
                <OptionsList
                    {...this.props}
                    gradient
                    renderDropdownItem={this.renderDropdownItem}
                />
            </>
        );
    }
}
